import { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { filter, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { MpApiErrorMessageComponent } from '../../../widgets/mp-api-error-message/mp-api-error-message.component';
import { MpSnackbarService } from '../../../widgets/mp-snackbar/mp-snackbar.service';
import { AuthService } from '@auth0/auth0-angular';
import { TranslateService } from '@ngx-translate/core';

export function MpApiErrorInterceptor(request: HttpRequest<unknown>,
                                      next: HttpHandlerFn): Observable<HttpEvent<unknown>> {

  const router: Router = inject(Router);
  const authService: AuthService = inject(AuthService);
  const snackBarService: MpSnackbarService = inject(MpSnackbarService);
  const injector: Injector = inject(Injector);

  handleSessionError(authService);

  return next(request).pipe(
    catchError((err) => {
      if (isMagicPalletRequest(request.url)) {
        switch (err.status) {
          case 400:
            openErrorInSnackbar(err, false, snackBarService);
            break;
          case 401:
            handle401Error(
              router,
              authService,
              injector);
            break;
          case 404:
            break;
          default:
            openErrorInSnackbar(err, true, snackBarService);
            break;
        }
      }
      return throwError(() => err);
    })
  );
}

function openErrorInSnackbar(err: any, autoDisapear = true, snackBar: MpSnackbarService): void {
  const errorKey = err.error.key;
  const variable = err.error.param;
  snackBar.openSnackbarFromComponent(MpApiErrorMessageComponent, {
    duration: autoDisapear ? 2000 : undefined,
    panelClass: 'error-snackbar',
    data: { isFunctionnalError: !autoDisapear, errorKey, variable }
  });
}

function handle401Error(router: Router,
                        authService: AuthService,
                        injector: Injector): void {
  const translateService = injector.get(TranslateService);
  const appLocation = router.routerState.snapshot.url;

  authService.isAuthenticated$.subscribe(isAuthenticated => {
    if (!isAuthenticated) {
      authService.loginWithRedirect({
        appState: { target: appLocation },
        authorizationParams: {
          ui_locales: translateService.currentLang,
          redirect_uri: window.location.origin,
          'ext-signup_url': `${window.location.origin}/onboarding`,
        }
      });
    }
  });
}

function handleSessionError(authService: AuthService): void {
  authService.error$.pipe(filter((err: any) =>
    (err?.error === 'login_required' || err?.error === 'consent_required')))
    .subscribe(() => authService.loginWithRedirect());
}

function isMagicPalletRequest(url: string): boolean {
  return environment.api_base_urls?.ms_defaults?.some((baseUrl) =>
    url.includes(baseUrl)
  );
}
